import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { DefaultService, GameFileInfo, CustomRuleOutput } from '../generated-api';
import { ArrowPathIcon, ExclamationTriangleIcon, DocumentIcon, PencilIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useJobPoller } from '../hooks/useJobPoller';

type RulesTab = 'summary' | 'setup' | 'turns';

const RULES_TABS: { key: RulesTab; label: string }[] = [
  { key: 'summary', label: 'Summary' },
  { key: 'setup', label: 'Set Up' },
  { key: 'turns', label: 'Turns' },
];

const SETUP_HEADERS = ['setup'];
const TURNS_HEADERS = ['turn structure', 'turn overview', 'turns', 'gameplay', 'player turns'];

function parseRulesSections(markdown: string): Record<RulesTab, string> {
  const cleaned = markdown
    .replace(/^```(?:markdown)?\n?/, '')
    .replace(/\n?```$/, '')
    .replace(/^#\s+.+\n*/m, ''); // Strip top-level h1 like "# Game Rules Summary"
  const sections = cleaned.split(/^## /m);
  const intro = sections[0]?.trim() || '';

  let setup = '';
  let turns = '';
  const summaryParts: string[] = [];

  if (intro) summaryParts.push(intro);

  for (let i = 1; i < sections.length; i++) {
    const section = sections[i];
    const firstNewline = section.indexOf('\n');
    const header = (firstNewline >= 0 ? section.slice(0, firstNewline) : section).trim().toLowerCase();
    const full = `## ${section}`;

    if (SETUP_HEADERS.some(h => header.startsWith(h))) {
      setup += (setup ? '\n\n' : '') + full;
    } else if (TURNS_HEADERS.some(h => header.startsWith(h))) {
      turns += (turns ? '\n\n' : '') + full;
    } else {
      summaryParts.push(full);
    }
  }

  return {
    summary: summaryParts.join('\n\n'),
    setup,
    turns,
  };
}

interface RulesPageProps {
  gameId: string;
  gameName: string;
}

const RulesPage: React.FC<RulesPageProps> = ({ gameId, gameName }) => {
  const [hasRules, setHasRules] = useState(false);
  const [rulesSummary, setRulesSummary] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [showOutdatedWarning, setShowOutdatedWarning] = useState(false);
  const [pdfFiles, setPdfFiles] = useState<GameFileInfo[]>([]);
  const [activeTab, setActiveTab] = useState<RulesTab>('summary');

  // Custom rules state
  const [customRules, setCustomRules] = useState<CustomRuleOutput[]>([]);
  const [isAddingRule, setIsAddingRule] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);
  const [newRuleTitle, setNewRuleTitle] = useState('');
  const [newRuleBody, setNewRuleBody] = useState('');
  const [savingRule, setSavingRule] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const loadRules = useCallback(async () => {
    try {
      setIsLoading(true);
      const rulesData = await DefaultService.getGameRulesGamesGameIdRulesGet({ gameId });
      setHasRules(rulesData.hasRules);
      setRulesSummary(rulesData.rulesSummary || '');
      setCustomRules(rulesData.customRules || []);

      const filesData = await DefaultService.listGameFilesGamesGameIdRulesFilesGet({ gameId });
      const hasExtractedText = filesData.some(f => f.fileType === 'extracted_text');
      const hasSummary = rulesData.rulesSummary !== null && rulesData.rulesSummary !== '';
      setShowOutdatedWarning(hasSummary && !hasExtractedText);
      setPdfFiles(filesData.filter(f => f.fileType === 'pdf'));
    } catch (error) {
      console.error('Failed to load rules:', error);
    } finally {
      setIsLoading(false);
    }
  }, [gameId]);

  const rulesPoller = useJobPoller(gameId, 'rules_processing', loadRules);

  useEffect(() => {
    loadRules();
  }, [loadRules]);

  const handleRegenerateSummary = async () => {
    if (!hasRules) return;
    try {
      const response = await DefaultService.generateRulesSummaryGamesGameIdRulesGenerateSummaryPost({ gameId });
      rulesPoller.startJob(response.jobId);
    } catch (error: any) {
      console.error('Failed to regenerate summary:', error);
      alert(error?.body?.detail || error.message || 'Failed to regenerate summary. Please try again.');
    }
  };

  const handleViewPdf = async (file: GameFileInfo) => {
    try {
      const response = await DefaultService.getPresignedUrlForRulesPdfDownloadGamesGameIdRulesFilesFileIdDownloadUrlGet({
        gameId: file.gameId,
        fileId: file.fileId,
      });
      window.open(response.url, '_blank');
    } catch (error: any) {
      console.error('Failed to get download URL:', error);
      alert(error?.body?.detail || error.message || 'Failed to open PDF.');
    }
  };

  // Custom rules handlers
  const handleAddRule = async () => {
    if (!newRuleTitle.trim()) return;
    setSavingRule(true);
    try {
      const created = await DefaultService.createCustomRuleGamesGameIdRulesCustomPost({
        gameId,
        requestBody: {
          title: newRuleTitle.trim(),
          body: newRuleBody.trim() || null,
        },
      });
      setCustomRules(prev => [...prev, created]);
      setNewRuleTitle('');
      setNewRuleBody('');
      setIsAddingRule(false);
    } catch (error: any) {
      console.error('Failed to create custom rule:', error);
      alert(error?.body?.detail || error.message || 'Failed to create rule.');
    } finally {
      setSavingRule(false);
    }
  };

  const handleUpdateRule = async (ruleId: string) => {
    if (!newRuleTitle.trim()) return;
    setSavingRule(true);
    try {
      const updated = await DefaultService.updateCustomRuleGamesGameIdRulesCustomRuleIdPut({
        gameId,
        ruleId,
        requestBody: {
          title: newRuleTitle.trim(),
          body: newRuleBody.trim() || null,
        },
      });
      setCustomRules(prev => prev.map(r => r.ruleId === ruleId ? updated : r));
      setEditingRuleId(null);
      setNewRuleTitle('');
      setNewRuleBody('');
    } catch (error: any) {
      console.error('Failed to update custom rule:', error);
      alert(error?.body?.detail || error.message || 'Failed to update rule.');
    } finally {
      setSavingRule(false);
    }
  };

  const handleDeleteRule = async (ruleId: string) => {
    try {
      await DefaultService.deleteCustomRuleGamesGameIdRulesCustomRuleIdDelete({ gameId, ruleId });
      setCustomRules(prev => prev.filter(r => r.ruleId !== ruleId));
      setDeleteConfirm(null);
    } catch (error: any) {
      console.error('Failed to delete custom rule:', error);
      alert(error?.body?.detail || error.message || 'Failed to delete rule.');
    }
  };

  const startEditRule = (rule: CustomRuleOutput) => {
    setEditingRuleId(rule.ruleId);
    setNewRuleTitle(rule.title);
    setNewRuleBody(rule.body || '');
    setIsAddingRule(false);
  };

  const cancelEdit = () => {
    setEditingRuleId(null);
    setIsAddingRule(false);
    setNewRuleTitle('');
    setNewRuleBody('');
  };

  const parsedSections = useMemo(() => parseRulesSections(rulesSummary), [rulesSummary]);

  const availableTabs = useMemo(
    () => RULES_TABS.filter(t => parsedSections[t.key].trim().length > 0),
    [parsedSections],
  );

  // Reset to first available tab when sections change
  useEffect(() => {
    if (availableTabs.length > 0 && !availableTabs.find(t => t.key === activeTab)) {
      setActiveTab(availableTabs[0].key);
    }
  }, [availableTabs, activeTab]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Rules Summary Section */}
      {rulesPoller.isProcessing ? (
        <div className="bg-surface border border-edge rounded-card p-6 shadow-card">
          <div className="flex items-center gap-3 py-12 justify-center text-muted">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500"></div>
            <span>{rulesPoller.statusText}</span>
          </div>
        </div>
      ) : rulesPoller.error ? (
        <div className="bg-surface border border-edge rounded-card p-6 shadow-card">
          <div className="p-4 bg-error-50 border border-error-500/20 rounded-button text-sm text-error-700">
            {rulesPoller.error}
          </div>
        </div>
      ) : hasRules && rulesSummary ? (
        <>
          {/* Tab navigation */}
          {availableTabs.length > 1 && (
            <div className="sticky top-0 z-10 bg-surface-subtle/95 backdrop-blur-sm -mx-4 px-4 py-2 sm:mx-0 sm:px-0 sm:static sm:bg-transparent sm:backdrop-blur-none sm:py-0">
              <div className="flex items-center gap-2">
                {availableTabs.map((tab) => (
                  <button
                    data-track="rules_tab"
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`flex-1 sm:flex-none px-4 py-2.5 rounded-full text-sm font-medium transition-colors ${
                      activeTab === tab.key
                        ? 'bg-primary-500 text-white shadow-sm'
                        : 'bg-surface text-body border border-edge hover:border-primary-300 hover:text-primary-700'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
                <div className="ml-auto flex items-center gap-1">
                  {showOutdatedWarning && (
                    <div className="group relative">
                      <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />
                      <div className="absolute right-0 top-8 hidden group-hover:block w-64 p-2 bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900 text-sm rounded shadow-lg z-10">
                        Summary may be outdated. No extracted text files found. Consider regenerating.
                      </div>
                    </div>
                  )}
                  {pdfFiles.length > 0 && (
                    pdfFiles.length === 1 ? (
                      <button
                        onClick={() => handleViewPdf(pdfFiles[0])}
                        className="p-2 text-disabled hover:text-red-600 hover:bg-red-50 rounded-full transition-colors"
                        title="View PDF Rules"
                      >
                        <DocumentIcon className="h-4 w-4" />
                      </button>
                    ) : (
                      <div className="relative group">
                        <button
                          className="p-2 text-disabled hover:text-red-600 hover:bg-red-50 rounded-full transition-colors"
                          title="View PDF Rules"
                        >
                          <DocumentIcon className="h-4 w-4" />
                        </button>
                        <div className="absolute right-0 top-full mt-1 hidden group-hover:block bg-surface border border-edge rounded-lg shadow-lg z-10 min-w-[200px]">
                          {pdfFiles.map(file => (
                            <button
                              data-track="rules_pdf_file"
                              key={file.fileId}
                              onClick={() => handleViewPdf(file)}
                              className="block w-full text-left px-4 py-2 text-sm text-body hover:bg-surface-hover first:rounded-t-lg last:rounded-b-lg"
                            >
                              {file.fileName}
                            </button>
                          ))}
                        </div>
                      </div>
                    )
                  )}
                  <button
                    onClick={handleRegenerateSummary}
                    disabled={rulesPoller.isProcessing}
                    className="p-2 text-disabled hover:text-primary-600 hover:bg-primary-50 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Regenerate Summary"
                  >
                    <ArrowPathIcon className={`h-4 w-4 ${rulesPoller.isProcessing ? 'animate-spin' : ''}`} />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Active tab content */}
          {availableTabs.map((tab) => {
            if (tab.key !== activeTab) return null;
            const content = parsedSections[tab.key];
            if (!content) return null;

            return (
              <div
                key={tab.key}
                className="bg-surface border border-edge rounded-card p-4 sm:p-6 shadow-card"
              >
                <div className="prose prose-stone prose-sm sm:prose-base max-w-none prose-headings:font-serif prose-headings:text-heading prose-a:text-primary-600 prose-a:no-underline hover:prose-a:underline">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {content}
                  </ReactMarkdown>
                </div>
              </div>
            );
          })}
        </>
      ) : (
        <div className="bg-surface border border-edge rounded-card p-6 shadow-card">
          <div className="text-center py-12 text-muted">
            <p className="text-lg mb-2">No rules uploaded yet</p>
            <p className="text-sm">Upload a PDF rulebook from the Files page</p>
          </div>
        </div>
      )}

      {/* House Rules Section */}
      <div className="bg-surface shadow rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-fluid-h2 font-bold text-heading">House Rules</h2>
          {!isAddingRule && !editingRuleId && (
            <button
              onClick={() => { setIsAddingRule(true); setNewRuleTitle(''); setNewRuleBody(''); }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
            >
              <PlusIcon className="h-4 w-4" />
              Add Rule
            </button>
          )}
        </div>

        {/* Add/Edit Form */}
        {(isAddingRule || editingRuleId) && (
          <div className="border border-edge rounded-xl p-4 mb-4">
            <div className="space-y-3">
              <input
                type="text"
                placeholder="Rule title"
                value={newRuleTitle}
                onChange={e => setNewRuleTitle(e.target.value)}
                maxLength={200}
                className="w-full px-3 py-2 border border-edge-input rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              <textarea
                placeholder="Description (optional)"
                value={newRuleBody}
                onChange={e => setNewRuleBody(e.target.value)}
                maxLength={2000}
                rows={3}
                className="w-full px-3 py-2 border border-edge-input rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-y"
              />
              <div className="flex justify-end gap-2">
                <button
                  onClick={cancelEdit}
                  className="px-3 py-1.5 text-sm font-medium text-body bg-surface border border-edge-input rounded-lg hover:bg-surface-hover transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => editingRuleId ? handleUpdateRule(editingRuleId) : handleAddRule()}
                  disabled={savingRule || !newRuleTitle.trim()}
                  className="px-3 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {savingRule ? 'Saving...' : editingRuleId ? 'Update' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Rules List */}
        {customRules.length === 0 && !isAddingRule ? (
          <div className="text-center py-8 text-muted">
            <p className="text-sm">No house rules added yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {customRules.map(rule => (
              <div key={rule.ruleId} className="border border-edge rounded-xl p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-heading">{rule.title}</h3>
                    {rule.body && (
                      <p className="mt-1 text-sm text-body whitespace-pre-wrap">{rule.body}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 ml-3 flex-shrink-0">
                    <button
                      onClick={() => startEditRule(rule)}
                      className="p-1.5 text-disabled hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                      title="Edit rule"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    {deleteConfirm === rule.ruleId ? (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleDeleteRule(rule.ruleId)}
                          className="px-2 py-1 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded transition-colors"
                        >
                          Confirm
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(null)}
                          className="px-2 py-1 text-xs font-medium text-body bg-surface-subtle hover:bg-surface-skeleton rounded transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setDeleteConfirm(rule.ruleId)}
                        className="p-1.5 text-disabled hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                        title="Delete rule"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RulesPage;
